import { useCallback } from 'react';
import { CohortNotifications } from 'App.types';

export interface SiteWithNotifications {
  captain_id: string;
  learn_url?: string;
  fleet_name?: string;
  aws_account?: string;
  notifications?: CohortNotifications;
  [key: string]: any;
}

export interface DowntimeConflictResult {
  hasConflicts: boolean;
  conflictingSites: SiteWithNotifications[];
}

export const useDowntimeConflictValidation = () => {
  const isSiteConflicting = useCallback(
    (downtimeDate: Date, site: SiteWithNotifications): boolean => {
      if (!site.notifications?.start_suggested_date || !site.notifications?.end_suggested_date) {
        return false;
      }
      const startDate = new Date(site.notifications.start_suggested_date);
      const endDate = new Date(site.notifications.end_suggested_date);
      return downtimeDate < startDate || downtimeDate > endDate;
    },
    [],
  );

  const getConflictingSites = useCallback(
    (downtimeDate: Date | null, sites: SiteWithNotifications[]): SiteWithNotifications[] => {
      if (!downtimeDate || sites.length === 0) return [];
      return sites.filter((site) => isSiteConflicting(downtimeDate, site));
    },
    [isSiteConflicting],
  );

  const validateSiteAgainstDowntime = useCallback(
    (downtimeDate: Date | string | null, site: SiteWithNotifications): boolean => {
      if (!downtimeDate) return true;
      const date = typeof downtimeDate === 'string' ? new Date(downtimeDate) : downtimeDate;
      return !isSiteConflicting(date, site);
    },
    [isSiteConflicting],
  );

  const validateDowntimeAgainstSites = useCallback(
    (newDowntimeDate: Date | string, sites: SiteWithNotifications[]): DowntimeConflictResult => {
      const date =
        typeof newDowntimeDate === 'string' ? new Date(newDowntimeDate) : newDowntimeDate;
      const conflicting = getConflictingSites(date, sites);
      return {
        hasConflicts: conflicting.length > 0,
        conflictingSites: conflicting,
      };
    },
    [getConflictingSites],
  );

  return {
    getConflictingSites,
    validateSiteAgainstDowntime,
    validateDowntimeAgainstSites,
    isSiteConflicting,
  };
};
